import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import { auth, db, isTeacher, resolveTeacherClassId, Teacher } from '../../../utils/firebase';
import {
  countAbsentStudents,
  DailyAttendanceStatus,
  getAttendanceDateKey,
  getDailyAttendancePath,
  isStudentAbsent
} from '../../../utils/classAttendance';
import { toggleStudentAbsentStatus, setStudentAbsentStatus } from '../../../utils/classAttendanceApi';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import InputField from '../../../components/ui/InputField';
import { isFeatureAvailable } from '../../../config/features';

interface StudentRow {
  uid: string;
  name: string;
  number: number;
}

const toStudentNumber = (data: Record<string, unknown>): number => {
  const raw = data.number ?? data.studentNumber;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string') {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const AttendanceManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;

  const [students, setStudents] = useState<StudentRow[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<Record<string, DailyAttendanceStatus>>({});
  const [selectedDateKey, setSelectedDateKey] = useState(() => getAttendanceDateKey());
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [savingStudentId, setSavingStudentId] = useState<string | null>(null);

  const classId = useMemo(() => resolveTeacherClassId(teacher), [teacher]);
  const authUid = auth.currentUser?.uid;
  const canUse = isTeacher(currentUser) && !!teacher?.schoolId && !!classId && !!authUid;

  const basePath = useMemo(() => {
    if (!teacher?.schoolId || !classId) return '';
    return `schools/${teacher.schoolId}/classes/${classId}`;
  }, [teacher?.schoolId, classId]);

  const selectedDateLabel = useMemo(() => {
    const parsed = new Date(`${selectedDateKey}T12:00:00`);
    return format(parsed, 'yyyy년 M월 d일 (EEE)', { locale: ko });
  }, [selectedDateKey]);

  const isToday = selectedDateKey === getAttendanceDateKey();

  useEffect(() => {
    if (!canUse || !basePath) {
      setLoadingStudents(false);
      return;
    }

    const studentsRef = collection(db, `${basePath}/students`);
    const unsubscribe = onSnapshot(
      query(studentsRef),
      (snapshot) => {
        const rows = snapshot.docs
          .map((studentDoc) => {
            const data = studentDoc.data() as Record<string, unknown>;
            if (data.approvalStatus === 'rejected') return null;
            return {
              uid: studentDoc.id,
              name: (data.name as string) || '이름 없음',
              number: toStudentNumber(data)
            };
          })
          .filter((row): row is StudentRow => row !== null)
          .sort((a, b) => a.number - b.number || a.name.localeCompare(b.name, 'ko'));
        setStudents(rows);
        setLoadingStudents(false);
      },
      (err) => {
        console.error('학생 목록 구독 오류:', err);
        toast.error('학생 목록을 불러오지 못했습니다.');
        setLoadingStudents(false);
      }
    );

    return () => unsubscribe();
  }, [canUse, basePath]);

  useEffect(() => {
    if (!canUse || !teacher.schoolId || !classId) return;

    const attendanceRef = doc(db, getDailyAttendancePath(teacher.schoolId, classId, selectedDateKey));
    const unsubscribe = onSnapshot(
      attendanceRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setAttendanceRecords({});
          return;
        }
        const data = snapshot.data();
        setAttendanceRecords((data.records as Record<string, DailyAttendanceStatus>) || {});
      },
      (err) => {
        console.error('출결 구독 오류:', err);
        toast.error('출결 정보를 불러오지 못했습니다.');
      }
    );

    return () => unsubscribe();
  }, [canUse, teacher.schoolId, classId, selectedDateKey]);

  const absentCount = useMemo(() => countAbsentStudents(attendanceRecords), [attendanceRecords]);
  const presentCount = Math.max(0, students.length - absentCount);

  const handleToggleAbsent = useCallback(
    async (studentId: string) => {
      if (!canUse || !teacher.schoolId || !classId || !authUid || savingStudentId) return;

      setSavingStudentId(studentId);
      try {
        await toggleStudentAbsentStatus({
          schoolId: teacher.schoolId,
          classId,
          dateKey: selectedDateKey,
          studentId,
          records: attendanceRecords,
          actorUid: authUid
        });
      } catch (err) {
        console.error('출결 저장 오류:', err);
        toast.error('출결 저장 중 오류가 발생했습니다.');
      } finally {
        setSavingStudentId(null);
      }
    },
    [attendanceRecords, authUid, canUse, classId, savingStudentId, selectedDateKey, teacher.schoolId]
  );

  const handleMarkAllPresent = useCallback(async () => {
    if (!canUse || !teacher.schoolId || !classId || !authUid || absentCount === 0) return;

    const absentIds = students
      .filter((student) => isStudentAbsent(attendanceRecords, student.uid))
      .map((student) => student.uid);

    setSavingStudentId('__bulk__');
    try {
      await Promise.all(
        absentIds.map((studentId) =>
          setStudentAbsentStatus({
            schoolId: teacher.schoolId,
            classId,
            dateKey: selectedDateKey,
            studentId,
            absent: false,
            actorUid: authUid
          })
        )
      );
      toast.success('모든 학생을 출석으로 변경했습니다.');
    } catch (err) {
      console.error('전체 출석 처리 오류:', err);
      toast.error('전체 출석 처리 중 오류가 발생했습니다.');
    } finally {
      setSavingStudentId(null);
    }
  }, [absentCount, attendanceRecords, authUid, canUse, classId, selectedDateKey, students, teacher.schoolId]);

  if (!canUse) {
    return (
      <div
        style={{
          padding: 'var(--gap-level-4)',
          maxWidth: 960,
          margin: '0 auto',
          fontFamily: 'var(--font-family-primary)'
        }}
      >
        <Card padding="24px" background="var(--Color-background-normal)">
          <p style={{ margin: 0, color: 'var(--Color-text-normal)' }}>
            담임 학급이 설정된 교사만 출결 관리를 사용할 수 있습니다.
          </p>
        </Card>
      </div>
    );
  }

  const homeroom = teacher.homeroomClass;

  return (
    <div
      style={{
        width: '100%',
        padding: 'var(--gap-level-4)',
        fontFamily: 'var(--font-family-primary)'
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginBottom: 'var(--gap-level-4)'
        }}
      >
        <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: '21px' }}>
          {homeroom?.grade}학년 {homeroom?.class}반
        </span>
        <h1
          style={{
            margin: 0,
            fontSize: '32px',
            fontWeight: 600,
            lineHeight: '48px',
            color: 'var(--Color-text-strong)'
          }}
        >
          출결 관리
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: '21px' }}>
          태블릿 자리표 화면과 실시간으로 연동됩니다. 결석은 자리표에서도 동일하게 표시됩니다.
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 'var(--gap-level-4)',
          marginBottom: 'var(--gap-level-4)'
        }}
      >
        <InputField
          label="날짜"
          type="date"
          value={selectedDateKey}
          onChange={setSelectedDateKey}
          size="medium"
          width="220px"
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <Button
            variant="secondary"
            size="medium"
            onClick={() => void handleMarkAllPresent()}
            disabled={absentCount === 0 || savingStudentId !== null}
          >
            전체 출석 처리
          </Button>
          {isFeatureAvailable('SEATING') && (
            <>
              <Button
                variant="secondary"
                size="medium"
                onClick={() => window.open('/class/kiosk/connect', '_blank', 'noopener,noreferrer')}
              >
                태블릿 연동
              </Button>
              <Button
                variant="secondary"
                size="medium"
                onClick={() => window.open('/class/teacher/SeatDisplay', '_blank', 'noopener,noreferrer')}
              >
                자리표 표시
              </Button>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 'var(--gap-level-4)',
          marginBottom: 'var(--gap-level-4)'
        }}
      >
        <Card padding="16px 20px" background="var(--Color-background-normal)">
          <div style={{ fontSize: '13px', color: 'var(--Color-text-assistive, #989898)' }}>기준일</div>
          <div style={{ fontSize: '18px', fontWeight: 600, marginTop: '4px' }}>{selectedDateLabel}</div>
          {isToday && (
            <div style={{ fontSize: '12px', color: 'var(--Color-fill-brand-color, #FF6F0F)', marginTop: '4px' }}>
              오늘
            </div>
          )}
        </Card>
        <Card padding="16px 20px" background="var(--Color-background-normal)">
          <div style={{ fontSize: '13px', color: 'var(--Color-text-assistive, #989898)' }}>전체</div>
          <div style={{ fontSize: '24px', fontWeight: 600, marginTop: '4px' }}>{students.length}명</div>
        </Card>
        <Card padding="16px 20px" background="var(--Color-background-normal)">
          <div style={{ fontSize: '13px', color: 'var(--Color-text-assistive, #989898)' }}>출석</div>
          <div style={{ fontSize: '24px', fontWeight: 600, marginTop: '4px', color: '#2E7D32' }}>
            {presentCount}명
          </div>
        </Card>
        <Card padding="16px 20px" background="var(--Color-background-normal)">
          <div style={{ fontSize: '13px', color: 'var(--Color-text-assistive, #989898)' }}>결석</div>
          <div style={{ fontSize: '24px', fontWeight: 600, marginTop: '4px', color: '#C62828' }}>
            {absentCount}명
          </div>
        </Card>
      </div>

      <Card padding="0" background="var(--Color-background-normal)">
        {loadingStudents ? (
          <div style={{ padding: '24px', color: 'var(--Color-text-normal)' }}>학생 목록을 불러오는 중…</div>
        ) : students.length === 0 ? (
          <div style={{ padding: '24px', color: 'var(--Color-text-normal)' }}>등록된 학생이 없습니다.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)' }}>
                  <th style={tableHeadCellStyle}>번호</th>
                  <th style={tableHeadCellStyle}>이름</th>
                  <th style={tableHeadCellStyle}>상태</th>
                  <th style={tableHeadCellStyle}>처리</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => {
                  const absent = isStudentAbsent(attendanceRecords, student.uid);
                  const isSaving =
                    savingStudentId === student.uid || savingStudentId === '__bulk__';

                  return (
                    <tr
                      key={student.uid}
                      style={{ borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)' }}
                    >
                      <td style={tableCellStyle}>{student.number > 0 ? `${student.number}번` : '-'}</td>
                      <td style={tableCellStyle}>{student.name}</td>
                      <td style={tableCellStyle}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '999px',
                            fontSize: '13px',
                            fontWeight: 600,
                            background: absent ? '#F3F3F3' : '#FFF1E7',
                            color: absent ? '#757575' : '#FF6F0F'
                          }}
                        >
                          {absent ? '결석' : '출석'}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <Button
                          variant={absent ? 'primary' : 'secondary'}
                          size="small"
                          disabled={isSaving}
                          onClick={() => void handleToggleAbsent(student.uid)}
                        >
                          {isSaving ? '저장 중…' : absent ? '출석 처리' : '결석 처리'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {isFeatureAvailable('SEATING') && (
        <p style={{ marginTop: '16px', fontSize: '13px', color: 'var(--Color-text-assistive, #989898)' }}>
          자리 배치와 함께 보려면{' '}
          <Link to="/class/teacher/SeatManagement" style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>
            자리 관리
          </Link>
          를 이용하세요.
        </p>
      )}
    </div>
  );
};

const tableHeadCellStyle: React.CSSProperties = {
  padding: '14px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--Color-text-assistive, #989898)',
  background: 'var(--Color-background-main, #F4F6FA)'
};

const tableCellStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: 'var(--Color-text-normal, #161616)'
};

export default AttendanceManagement;
